import React, { useState } from 'react';
import { Text } from 'office-ui-fabric-react/lib/Text';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react';
import SearchBar from '../SearchBar';
import Module from '../Module';
import ModuleContentList from '../ModuleContentList';
import AdvanceGroupsList from '../AdvanceGroupsList';
import Pagination from '../Pagination';
import { fetchAdvanceGroups } from '../../actions/advanceGroups';
import styles from './AdvanceGroupListTab.style';
import theme from '../../theme.style';
import useAPI from '../../hooks/useAPI';
import { ContractFilterStatus } from '../../constants';

const AdvanceGroupListTab = ({ hiddenColumns, contractId, contractCurrency }) => {
  const [resetPagination, setResetPagination] = useState(false);
  const [filterState, setFilterState] = useState({ contractId });
  const [currentPageVal, setCurrentPageVal] = useState(0);
  const pageLimit = 20;
  const advancegroups = useSelector(state =>
    get(state, 'pagination.advancegroups.results', []).map(_id =>
      get(state, `entities.advancegroups.${_id}`),
    ),
  );

  const totalCount = useSelector(state => get(state, 'pagination.advancegroups.totalCount', 0));

  const advancegroupsList = useAPI(() => fetchAdvanceGroups(filterState), [filterState]);

  const statusFilterOptions = [
    { key: 0, text: 'All Advance Groups', status: '' },
    { key: 1, text: 'Contract In Setup', status: ContractFilterStatus.InSetup },
    { key: 2, text: 'Contract Setup Complete', status: ContractFilterStatus.SetupComplete },
    { key: 3, text: 'Active Contract', status: ContractFilterStatus.Active },
    { key: 4, text: 'Expired Contract', status: ContractFilterStatus.Expired },
  ];

  const updateAdvanceGroupFilters = advanceGroupFilters => {
    setFilterState(advanceGroupFilters);
  };

  const updateSearchFilter = async sValue => {
    const newFilterState = {
      ...filterState,
      query: sValue,
      start: null,
    };
    updateAdvanceGroupFilters(newFilterState);
  };

  const handleSearch = sValue => {
    setResetPagination(true);
    updateSearchFilter(sValue);
  };

  const handleClear = () => {
    setResetPagination(true);
    updateSearchFilter();
  };

  const onStatusChange = async (event, option) => {
    const newFilterState = {
      ...filterState,
      status: option.status,
      start: null,
    };
    setResetPagination(true);
    updateAdvanceGroupFilters(newFilterState);
  };

  const handlePageChange = paginationData => {
    setResetPagination(false);
    setCurrentPageVal(paginationData.currentPageIndex);
    const newFilterState = {
      ...filterState,
      start: paginationData.currentPageIndex * paginationData.pageLimit,
      count: paginationData.pageLimit,
    };

    updateAdvanceGroupFilters(newFilterState);
  };

  return (
    <>
      {!contractId && (
        <div className={styles.searchSection}>
          <SearchBar
            className={styles.search}
            placeholder="Search advance groups"
            ariaLabel="Search"
            data-testid="search-input"
            onChange={handleSearch}
            onClear={handleClear} // when you click X
          />
          <Dropdown
            data-testid="status-dropdown"
            ariaLabel="StatusFilter"
            placeholder="All Advance Groups"
            label="Status"
            options={statusFilterOptions}
            className={styles.filter}
            styles={{
              title: { backgroundColor: theme.palette.neutralWhite },
              dropdown: { fontWeight: 'normal' },
              dropdownItem: { fontWeight: 'normal' },
            }}
            onChange={onStatusChange}
          />
        </div>
      )}

      {(() => {
        if (advancegroupsList.isLoading) {
          return (
            <Module>
              <ModuleContentList>
                <AdvanceGroupsList
                  items={[]}
                  shimmered
                  enableShimmer
                  noBorderBottom
                  data-testid="advancegroups-list-items-shimmer"
                  hiddenColumns={hiddenColumns}
                />
              </ModuleContentList>
            </Module>
          );
        }

        if (advancegroupsList.error) {
          return (
            <MessageBar
              isMultiline={false}
              messageBarType={MessageBarType.error}
              messageBarIconProps={{ iconName: 'Info' }}
            >
              Something went wrong while loading advance groups. Please try refreshing the page.
            </MessageBar>
          );
        }

        if (advancegroups.length === 0) {
          return (
            <div className={styles.resultsMessage}>
              <Text as="h4" block>
                No Results
              </Text>

              <Text as="p" block className={styles.noResultsSentence}>
                Try searching by Advance Group Name, Contract Id, Contract Name, or Partner Name
              </Text>
            </div>
          );
        }

        return (
          <>
            <div className={styles.resultsMessage}>
              <Text as="h4" block>
                {totalCount} Result(s)
              </Text>
            </div>

            <Module>
              <ModuleContentList>
                <AdvanceGroupsList
                  items={advancegroups}
                  contractCurrency={contractCurrency}
                  shimmered
                  enableShimmer={false}
                  noBorderBottom
                  data-testid="advancegroups-list-items"
                  hiddenColumns={hiddenColumns}
                />
              </ModuleContentList>
              {((contractId && totalCount && totalCount > pageLimit) || !contractId) && (
                <Pagination
                  totalRecords={totalCount}
                  pageLimit={pageLimit}
                  handlePageChange={handlePageChange}
                  resetPagination={resetPagination}
                  currentPageVal={currentPageVal}
                />
              )}
            </Module>
          </>
        );
      })()}
    </>
  );
};

AdvanceGroupListTab.propTypes = {
  hiddenColumns: PropTypes.arrayOf(PropTypes.string),
  contractId: PropTypes.string,
  contractCurrency: PropTypes.string,
};

AdvanceGroupListTab.defaultProps = {
  hiddenColumns: [],
  contractId: undefined,
  contractCurrency: undefined,
};

export default AdvanceGroupListTab;
